import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { AppIconButton } from '../components/ui';
import {
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import metricsService, {
  EndpointMetric,
  SystemMetrics,
  DatabaseMetrics,
} from '../services/metricsService';

interface DashboardStats {
  health: 'UP' | 'DOWN' | 'UNKNOWN';
  totalRequests: number;
  error4xx: number;
  error5xx: number;
  errorRate: number;
  endpoints: EndpointMetric[];
  systemMetrics: SystemMetrics | null;
  databaseMetrics: DatabaseMetrics | null;
}

export default function ApiPerformanceDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchMetrics = async () => {
    try {
      setError(null);

      // Fetch all metrics in parallel
      const [health, endpoints, systemMetrics, databaseMetrics, errorRate] =
        await Promise.all([
          metricsService.getHealth(),
          metricsService.getEndpointMetrics(),
          metricsService.getSystemMetrics().catch(() => null),
          metricsService.getDatabaseMetrics().catch(() => null),
          metricsService.getErrorRate(),
        ]);

      const totalRequests = errorRate.total;
      const error4xx = errorRate.error4xx;
      const error5xx = errorRate.error5xx;
      const errorRatePercent = totalRequests > 0
        ? ((error4xx + error5xx) / totalRequests) * 100
        : 0;

      setStats({
        health: health.status as 'UP' | 'DOWN' | 'UNKNOWN',
        totalRequests,
        error4xx,
        error5xx,
        errorRate: errorRatePercent,
        endpoints,
        systemMetrics,
        databaseMetrics,
      });

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError('Failed to fetch metrics. Please check if the backend is running and Actuator endpoints are accessible.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();

    // Auto-refresh every 10 seconds
    let interval: NodeJS.Timeout | null = null;
    if (autoRefresh) {
      interval = setInterval(fetchMetrics, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (milliseconds: number): string => {
    if (milliseconds < 1) return `${(milliseconds * 1000).toFixed(2)}μs`;
    if (milliseconds < 1000) return `${milliseconds.toFixed(2)}ms`;
    return `${(milliseconds / 1000).toFixed(2)}s`;
  };

  const formatPercent = (value: number): string => {
    return `${(value * 100).toFixed(2)}%`;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box p={3}>
        <Alert severity="warning">No metrics data available</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            API Performance Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            label={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            color={autoRefresh ? 'success' : 'default'}
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
          />
          <AppIconButton
            icon={<RefreshIcon />}
            tooltip="Refresh now"
            variant="primary"
            onClick={fetchMetrics}
          />
        </Box>
      </Box>

      {/* Health Status */}
      <Grid container spacing={{ xs: 1, sm: 3 }} mb={{ xs: 2, sm: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                {stats.health === 'UP' ? (
                  <CheckCircleIcon color="success" sx={{ fontSize: { xs: 18, sm: 24 } }} />
                ) : (
                  <ErrorIcon color="error" sx={{ fontSize: { xs: 18, sm: 24 } }} />
                )}
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1.25rem' }, fontWeight: 600 }}>Health</Typography>
              </Box>
              <Typography sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' }, fontWeight: 700 }} color={stats.health === 'UP' ? 'success.main' : 'error.main'}>
                {stats.health}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <SpeedIcon color="primary" sx={{ fontSize: { xs: 18, sm: 24 } }} />
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1.25rem' }, fontWeight: 600 }}>Requests</Typography>
              </Box>
              <Typography sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' }, fontWeight: 700 }}>{stats.totalRequests.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <ErrorIcon color="warning" sx={{ fontSize: { xs: 18, sm: 24 } }} />
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1.25rem' }, fontWeight: 600 }}>Error Rate</Typography>
              </Box>
              <Typography
                sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' }, fontWeight: 700 }}
                color={stats.errorRate > 5 ? 'error.main' : stats.errorRate > 1 ? 'warning.main' : 'success.main'}
              >
                {stats.errorRate.toFixed(2)}%
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                4xx: {stats.error4xx} | 5xx: {stats.error5xx}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <TimerIcon color="info" sx={{ fontSize: { xs: 18, sm: 24 } }} />
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1.25rem' }, fontWeight: 600 }}>Endpoints</Typography>
              </Box>
              <Typography sx={{ fontSize: { xs: '1.25rem', sm: '2.125rem' }, fontWeight: 700 }}>{stats.endpoints.length}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                Monitored
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Metrics */}
      {stats.systemMetrics && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <MemoryIcon color="primary" />
              <Typography variant="h6">System Metrics</Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  JVM Memory Usage
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <LinearProgress
                    variant="determinate"
                    value={(stats.systemMetrics.jvmMemoryUsed / stats.systemMetrics.jvmMemoryMax) * 100}
                    sx={{ flex: 1, height: 8, borderRadius: 1 }}
                  />
                  <Typography variant="body2" sx={{ minWidth: 150 }}>
                    {formatBytes(stats.systemMetrics.jvmMemoryUsed)} / {formatBytes(stats.systemMetrics.jvmMemoryMax)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  CPU Usage
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <LinearProgress
                    variant="determinate"
                    value={stats.systemMetrics.cpuUsage * 100}
                    sx={{ flex: 1, height: 8, borderRadius: 1 }}
                    color={stats.systemMetrics.cpuUsage > 0.8 ? 'error' : 'primary'}
                  />
                  <Typography variant="body2" sx={{ minWidth: 150 }}>
                    Process: {formatPercent(stats.systemMetrics.cpuUsage)}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2} mt={1}>
                  <LinearProgress
                    variant="determinate"
                    value={stats.systemMetrics.systemCpuUsage * 100}
                    sx={{ flex: 1, height: 8, borderRadius: 1 }}
                    color={stats.systemMetrics.systemCpuUsage > 0.8 ? 'error' : 'info'}
                  />
                  <Typography variant="body2" sx={{ minWidth: 150 }}>
                    System: {formatPercent(stats.systemMetrics.systemCpuUsage)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">Threads</Typography>
                <Typography variant="body1">
                  {stats.systemMetrics.threadCount} / {stats.systemMetrics.threadPeak} (peak)
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">Classes Loaded</Typography>
                <Typography variant="body1">{stats.systemMetrics.classesLoaded.toLocaleString()}</Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">GC Pauses</Typography>
                <Typography variant="body1">
                  {stats.systemMetrics.gcPauseCount} ({formatDuration(stats.systemMetrics.gcPauseTime)})
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Database Metrics */}
      {stats.databaseMetrics && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <StorageIcon color="primary" />
              <Typography variant="h6">Database Connection Pool (HikariCP)</Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Connection Pool Usage
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <LinearProgress
                    variant="determinate"
                    value={(stats.databaseMetrics.activeConnections / stats.databaseMetrics.maxConnections) * 100}
                    sx={{ flex: 1, height: 8, borderRadius: 1 }}
                    color={
                      stats.databaseMetrics.activeConnections / stats.databaseMetrics.maxConnections > 0.8
                        ? 'error'
                        : 'success'
                    }
                  />
                  <Typography variant="body2" sx={{ minWidth: 150 }}>
                    {stats.databaseMetrics.activeConnections} / {stats.databaseMetrics.maxConnections}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Active</Typography>
                    <Typography variant="h6" color="success.main">
                      {stats.databaseMetrics.activeConnections}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Idle</Typography>
                    <Typography variant="h6" color="info.main">
                      {stats.databaseMetrics.idleConnections}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Pending</Typography>
                    <Typography variant="h6" color={stats.databaseMetrics.pendingThreads > 0 ? 'warning.main' : 'text.primary'}>
                      {stats.databaseMetrics.pendingThreads}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {stats.databaseMetrics.connectionTimeout > 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Connection timeouts detected: {stats.databaseMetrics.connectionTimeout}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Endpoint Performance Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Endpoint Performance (Top 20 Slowest)
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Method</TableCell>
                  <TableCell>Endpoint</TableCell>
                  <TableCell align="right">Count</TableCell>
                  <TableCell align="right">Mean</TableCell>
                  <TableCell align="right">Max</TableCell>
                  <TableCell align="right">P95</TableCell>
                  <TableCell align="right">P99</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.endpoints.slice(0, 20).map((endpoint, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Chip
                        label={endpoint.method}
                        size="small"
                        color={
                          endpoint.method === 'GET'
                            ? 'info'
                            : endpoint.method === 'POST'
                            ? 'success'
                            : endpoint.method === 'PUT'
                            ? 'warning'
                            : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {endpoint.endpoint}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{endpoint.count.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={
                          endpoint.mean > 1000
                            ? 'error'
                            : endpoint.mean > 500
                            ? 'warning.main'
                            : 'text.primary'
                        }
                      >
                        {formatDuration(endpoint.mean)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatDuration(endpoint.max)}</TableCell>
                    <TableCell align="right">
                      {endpoint.p95 ? formatDuration(endpoint.p95) : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      {endpoint.p99 ? formatDuration(endpoint.p99) : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
